import { useEffect, useState, useCallback } from 'react';
import { api, ApiError, asList } from '../lib/api';
import type {
  CampusView, InstitutionView, AcademicClassView, SubjectView,
  SessionView, SessionRosterView, GenerateSessionsResult,
} from '../lib/types';

const monthStart = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);
const STATUSES = ['Present', 'Absent', 'Late', 'Excused'] as const;

const STATE_STYLE: Record<string, string> = {
  Scheduled: 'pill-muted', Held: 'pill-ok', Cancelled: 'pill-danger',
};

export function SessionsPage() {
  const [institutions, setInstitutions] = useState<InstitutionView[]>([]);
  const [campuses, setCampuses] = useState<CampusView[]>([]);
  const [institutionId, setInstitutionId] = useState('');
  const [classes, setClasses] = useState<AcademicClassView[]>([]);
  const [classId, setClassId] = useState('');
  const [subjects, setSubjects] = useState<Record<string, SubjectView>>({});
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [sessions, setSessions] = useState<SessionView[]>([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [marking, setMarking] = useState<SessionView | null>(null);

  useEffect(() => {
    Promise.all([
      api.get<InstitutionView[]>('/api/institutions'),
      api.get<CampusView[]>('/api/campuses'),
    ]).then(([i, c]) => { setInstitutions(i); setCampuses(c); if (i.length === 1) setInstitutionId(i[0].id); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!institutionId) { setClasses([]); setSubjects({}); return; }
    api.get<AcademicClassView[]>(`/api/classes?institutionId=${institutionId}`).then((r) => setClasses(asList(r))).catch(() => {});
    api.get<SubjectView[]>(`/api/subjects?institutionId=${institutionId}`)
      .then((list) => setSubjects(Object.fromEntries(list.map((s) => [s.id, s])))).catch(() => {});
  }, [institutionId]);

  const loadSessions = useCallback(async () => {
    if (!classId) { setSessions([]); return; }
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ academicClassId: classId, from, to });
      setSessions(await api.get<SessionView[]>(`/api/period-attendance/sessions?${params}`));
    } finally { setLoading(false); }
  }, [classId, from, to]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const generate = async () => {
    if (!classId) return;
    setError(null); setMsg(null);
    try {
      const res = await api.post<GenerateSessionsResult>('/api/period-attendance/sessions/generate', {
        academicClassId: classId, from, to,
      });
      setMsg(`Generated ${res.created} session${res.created === 1 ? '' : 's'} (${res.skipped} already existed).`);
      loadSessions();
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Could not generate sessions.'); }
  };

  const cancel = async (s: SessionView) => {
    await api.post(`/api/period-attendance/sessions/${s.id}/cancel`);
    loadSessions();
  };
  const reopen = async (s: SessionView) => {
    await api.post(`/api/period-attendance/sessions/${s.id}/reopen`);
    loadSessions();
  };

  const campusName = (id: string) => campuses.find((c) => c.id === id)?.name ?? '';
  const subjName = (id: string) => subjects[id]?.code ?? '—';

  // group sessions by date for readability
  const byDate = sessions.reduce((acc, s) => { (acc[s.date] ??= []).push(s); return acc; }, {} as Record<string, SessionView[]>);
  const dates = Object.keys(byDate).sort();

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Engineering</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Class sessions</h1>
        <p style={{ color: 'var(--ink-2)', marginTop: 4, maxWidth: 640 }}>
          Generate dated sessions from the class timetable, then mark attendance per period.
          Only marked (held) sessions count toward eligibility.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <label className="label">Institution</label>
          <select className="input" value={institutionId} onChange={(e) => { setInstitutionId(e.target.value); setClassId(''); }} style={{ minWidth: 200 }}>
            <option value="">Select</option>
            {institutions.map((i) => <option key={i.id} value={i.id}>{i.name}{campusName(i.campusId) ? ` · ${campusName(i.campusId)}` : ''}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Class</label>
          <select className="input" value={classId} onChange={(e) => setClassId(e.target.value)} disabled={!institutionId} style={{ minWidth: 180 }}>
            <option value="">Select</option>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
        <div><label className="label">From</label><input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ width: 160 }} /></div>
        <div><label className="label">To</label><input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ width: 160 }} /></div>
        <button className="btn btn-accent" onClick={generate} disabled={!classId}>Generate sessions</button>
      </div>

      {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}
      {msg && <div className="pill pill-ok" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', marginBottom: 12, justifyContent: 'flex-start' }}>{msg}</div>}

      {loading && <p style={{ color: 'var(--ink-3)' }}>Loading…</p>}

      {!loading && classId && sessions.length === 0 && (
        <div className="card" style={{ padding: 28, textAlign: 'center', color: 'var(--ink-2)' }}>
          No sessions in this range yet. Generate them from the timetable above.
        </div>
      )}

      {dates.map((date) => (
        <div key={date} style={{ marginBottom: 18 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink-2)', marginBottom: 8 }}>
            {new Date(date).toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'short' })}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10 }}>
            {byDate[date].sort((a, b) => a.periodNumber - b.periodNumber).map((s) => (
              <div key={s.id} className="card" style={{ padding: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span className="mono" style={{ fontSize: 13, fontWeight: 600 }}>P{s.periodNumber} · {subjName(s.subjectId)}</span>
                  <span className={`pill ${STATE_STYLE[s.state] ?? 'pill-muted'}`} style={{ fontSize: 11 }}>{s.state}</span>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {s.state !== 'Cancelled' ? (
                    <>
                      <button className="btn" style={{ flex: 1, justifyContent: 'center', padding: '5px 8px', fontSize: 12 }} onClick={() => setMarking(s)}>
                        {s.state === 'Held' ? 'Edit' : 'Mark'}
                      </button>
                      <button className="btn" style={{ padding: '5px 8px', fontSize: 12 }} onClick={() => cancel(s)} title="Cancel this class">✕</button>
                    </>
                  ) : (
                    <button className="btn" style={{ flex: 1, justifyContent: 'center', padding: '5px 8px', fontSize: 12 }} onClick={() => reopen(s)}>Re-open</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {marking && (
        <MarkSessionDrawer session={marking} subjectCode={subjName(marking.subjectId)}
          onClose={() => setMarking(null)} onDone={() => { setMarking(null); loadSessions(); }} />
      )}
    </div>
  );
}

function MarkSessionDrawer({ session, subjectCode, onClose, onDone }: {
  session: SessionView; subjectCode: string; onClose: () => void; onDone: () => void;
}) {
  const [roster, setRoster] = useState<SessionRosterView | null>(null);
  const [marks, setMarks] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<SessionRosterView>(`/api/period-attendance/sessions/${session.id}/roster`)
      .then((r) => { setRoster(r); setMarks(Object.fromEntries(r.students.map((s) => [s.studentId, s.status]))); })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Could not load the roster.'));
  }, [session.id]);

  const save = async () => {
    if (!roster) return;
    setBusy(true); setError(null);
    try {
      const entries = roster.students.map((s) => ({ studentId: s.studentId, status: marks[s.studentId] ?? 'Present', remark: null }));
      await api.post(`/api/period-attendance/sessions/${session.id}/mark`, { entries });
      onDone();
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Could not save.'); }
    finally { setBusy(false); }
  };

  const markAll = (status: string) => roster && setMarks(Object.fromEntries(roster.students.map((s) => [s.studentId, status])));

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,12,18,.45)', zIndex: 50, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ width: 460, maxWidth: '100%', height: '100%', borderRadius: 0, padding: 24, overflowY: 'auto' }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Mark attendance</div>
        <h2 className="h-display" style={{ fontSize: 20, marginBottom: 4 }}>P{session.periodNumber} · {subjectCode}</h2>
        <div style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 16 }}>
          {new Date(session.date).toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'short' })}
        </div>

        {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}

        {roster && (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <button className="btn" style={{ padding: '5px 10px', fontSize: 13 }} onClick={() => markAll('Present')}>All present</button>
              <button className="btn" style={{ padding: '5px 10px', fontSize: 13 }} onClick={() => markAll('Absent')}>All absent</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
              {roster.students.map((s) => (
                <div key={s.studentId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--line-2)' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.fullName}</div>
                    <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{s.admissionNumber}</div>
                  </div>
                  <div style={{ display: 'inline-flex', border: '1px solid var(--line)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
                    {STATUSES.map((st) => {
                      const active = (marks[s.studentId] ?? 'Present') === st;
                      const color = st === 'Present' ? 'var(--ok)' : st === 'Absent' ? 'var(--danger)' : st === 'Late' ? 'var(--warn)' : 'var(--ink-3)';
                      return (
                        <button key={st} onClick={() => setMarks((m) => ({ ...m, [s.studentId]: st }))}
                          title={st}
                          style={{ border: 'none', padding: '5px 9px', fontSize: 12, fontWeight: active ? 700 : 500, cursor: 'pointer', background: active ? color : 'transparent', color: active ? '#fff' : 'var(--ink-3)' }}>
                          {st[0]}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              {roster.students.length === 0 && (
                <div style={{ color: 'var(--ink-3)', fontSize: 13, padding: '12px 0' }}>
                  No students linked to this class. Assign students to this class first.
                </div>
              )}
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn" style={{ flex: 1, justifyContent: 'center' }} onClick={onClose}>Cancel</button>
          <button className="btn btn-accent" style={{ flex: 1, justifyContent: 'center' }} disabled={busy || !roster || roster.students.length === 0} onClick={save}>
            {busy ? 'Saving…' : 'Save & mark held'}
          </button>
        </div>
      </div>
    </div>
  );
}
